import React, { CSSProperties, Fragment } from 'react';
import { Link } from 'react-router-dom';

import { ArrowRight, CircleAlert, Lock } from 'lucide-react';
import { DynamicIcon, IconName } from 'lucide-react/dynamic';

import { useGetScenarios } from '../../../hooks/mbts-ux';

import { getScenarioColor } from '../../../helpers/misc.helper';

export const MBTSScenarios = () => {
    const { data: scenarios, isLoading, error: scenariosError } = useGetScenarios();

    const error = (scenariosError as any)?.response?.data;
    const entitlementError =
        error?.statusCode === 403
            ? error.message || 'You are not entitled to access this product.'
            : null;

    return (
        <div className='mbts_scenarios__page'>
            <p className='text-4xl text-bold color--primary mb-md'>Effective Challenge Simulations</p>
            <p className='text-lg color--txt-secondary'>Master difficult conversations and stakeholder management through immersive, AI-powered scenarios</p>
            <div className='card card--md card--radius-md d-flex gap-lg my-2xl'>
                <div>
                    <CircleAlert className='color--info' />
                </div>
                <div>
                    <p className='text-semibold mb-sm'>Privacy Notice</p>
                    <p className='text-sm color--txt-tertiary'>
                        Your responses during these simulations are recorded for analysis and feedback purposes. This data helps provide personalized insights to improve your communication skills. All data is processed securely and used solely for educational purposes.
                    </p>
                </div>
            </div>

            {isLoading && !scenarios && !entitlementError && !error && (
                <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <div className='list-loader' />
                    <p className='text-medium color--txt-secondary'>Loading simulations</p>
                </div>
            )}

            {!isLoading && !!entitlementError && (
                <div className='mbts_scenarios__top_sec mt-2xl'>
                    <div className='mbts_scenarios__top_sec__icon mbts_scenarios__top_sec__icon--locked'>
                        <Lock />
                    </div>
                    <p className='text-3xl text-bold mb-xs text-center'>Access Restricted</p>
                    <p className='color--txt-tertiary text-center'>{entitlementError}</p>
                </div>
            )}
            {!isLoading && !entitlementError && !!error && (
                <div className='mt-2xl d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <CircleAlert className='color--error w-3xl h-3xl' />
                    <p className='text-2xl text-semibold text-center'>Error occurred</p>
                    <p className='color--txt-tertiary text-center'>{error?.message || 'An error occurred while loading the simulations.'}</p>
                </div>
            )}

            {!isLoading && !scenarios?.length && !entitlementError && !error && (
                <p className='text-lg text-semibold color--txt-tertiary text-center'>No simulations found!</p>
            )}
            {!isLoading && !!scenarios?.length && !entitlementError && !error && (
                <Fragment>
                    <p className='text-sm text-semibold color--txt-disabled tracking-wider mb-lg'>AVAILABLE SIMULATIONS</p>
                    <div className='mbts_scenarios__scenario_cards'>
                        {scenarios?.map((scenario) => (
                            <Link
                                key={scenario.id}
                                to={scenario.is_entitled ? `/mbts/${scenario.id}/personas` : '#'}
                                className={`card card--md card--radius-md ${scenario.is_entitled ? 'card--hover-shadow' : 'mbts_scenarios__scenario_card--disabled'} mbts_scenarios__scenario_card `}
                                style={{
                                    '--scenario-theme-color-main': getScenarioColor(scenario).main,
                                    '--scenario-theme-color-bg': getScenarioColor(scenario).bg
                                } as CSSProperties}
                            >
                                <div className='mbts_scenarios__scenario_card__icon'>
                                    <DynamicIcon name={scenario.icon as IconName} />
                                </div>
                                <div>
                                    <div className='d-flex justify-content-between gap-sm mb-sm'>
                                        <p className='text-xl text-bold color--txt-secondary'>{scenario.title}</p>
                                        <div className={`mbts_scenarios__scenario_card__status mbts_scenarios__scenario_card__status--${scenario.is_entitled ? 'active' : 'inactive'}`}>
                                            <p className='text-sm text-medium'>
                                                {scenario.is_entitled ? 'Available' : 'Not Available'}
                                            </p>
                                        </div>
                                    </div>
                                    <p className='color--txt-muted'>{scenario.description}</p>
                                    <div className='mbts_scenarios__scenario_card__bottom mt-lg'>
                                        <p className='text-medium color--primary'>{scenario.status === 'completed' ? 'Retake' : scenario.status === 'in-progress' ? 'Continue' : 'Start'} Scenario</p>
                                        <ArrowRight className='w-lg h-lg color--primary' />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Fragment>
            )}
        </div>
    );
};